import React, { useEffect, useRef, useState } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;

type View = { scale: number; x: number; y: number };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Full-screen photo viewer: pinch or wheel to zoom, drag to pan, double-tap
 * to toggle zoom. Gestures are handled here (touch-action: none) so pinching
 * zooms the photo instead of the whole page.
 */
const PhotoViewer = ({ src, onClose }: { src: string; onClose: () => void }) => {
	const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
	const pointers = useRef(new Map<number, { x: number; y: number }>());
	const gesture = useRef<{ dist: number; scale: number; mid: { x: number; y: number }; view: View } | null>(null);
	const lastTap = useRef(0);
	const viewRef = useRef(view);
	viewRef.current = view;

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
		window.addEventListener('keydown', onKey);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			window.removeEventListener('keydown', onKey);
			document.body.style.overflow = prevOverflow;
		};
	}, [onClose]);

	const settle = (v: View): View => (v.scale <= MIN_SCALE ? { scale: 1, x: 0, y: 0 } : v);

	const startGesture = () => {
		const view = viewRef.current;
		const pts = Array.from(pointers.current.values());
		if (pts.length === 2) {
			const [a, b] = pts;
			gesture.current = {
				dist: Math.hypot(a.x - b.x, a.y - b.y),
				scale: view.scale,
				mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
				view,
			};
		} else if (pts.length === 1) {
			gesture.current = { dist: 0, scale: view.scale, mid: pts[0], view };
		}
	};

	const onPointerDown = (e: React.PointerEvent) => {
		try {
			(e.target as Element).setPointerCapture?.(e.pointerId);
		} catch {
			/* pointer already gone; the gesture still works without capture */
		}
		pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

		if (pointers.current.size === 1 && e.pointerType !== 'mouse') {
			const now = Date.now();
			if (now - lastTap.current < 280) {
				setView((v) => (v.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: DOUBLE_TAP_SCALE, x: 0, y: 0 }));
				lastTap.current = 0;
				return;
			}
			lastTap.current = now;
		}
		startGesture();
	};

	const onPointerMove = (e: React.PointerEvent) => {
		if (!pointers.current.has(e.pointerId)) return;
		pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		const g = gesture.current;
		if (!g) return;
		const pts = Array.from(pointers.current.values());

		if (pts.length === 2 && g.dist > 0) {
			const [a, b] = pts;
			const dist = Math.hypot(a.x - b.x, a.y - b.y);
			const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
			setView({
				scale: clamp((g.scale * dist) / g.dist, MIN_SCALE, MAX_SCALE),
				x: g.view.x + (mid.x - g.mid.x),
				y: g.view.y + (mid.y - g.mid.y),
			});
		} else if (pts.length === 1 && g.view.scale > 1) {
			setView({ ...g.view, x: g.view.x + (pts[0].x - g.mid.x), y: g.view.y + (pts[0].y - g.mid.y) });
		}
	};

	const onPointerUp = (e: React.PointerEvent) => {
		pointers.current.delete(e.pointerId);
		if (pointers.current.size === 0) {
			gesture.current = null;
			setView(settle(viewRef.current));
		} else {
			startGesture();
		}
	};

	const onWheel = (e: React.WheelEvent) => {
		const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
		setView((v) => settle({ ...v, scale: clamp(v.scale * factor, MIN_SCALE, MAX_SCALE) }));
	};

	return (
		<div className='fixed inset-0 z-[2000] bg-black flex flex-col' role='dialog' aria-modal='true' aria-label='Photo'>
			<div className='flex items-center justify-between px-2 py-2 text-white shrink-0'>
				<button
					type='button'
					onClick={onClose}
					className='w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/10'
					aria-label='Close photo'
				>
					<i className='fa-solid fa-xmark text-2xl'></i>
				</button>
				<span className='text-xs text-white/60 pr-[0.75rem]'>Pinch or double-tap to zoom</span>
			</div>
			<div
				className='flex-1 overflow-hidden flex items-center justify-center select-none'
				style={{ touchAction: 'none' }}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerUp}
				onDoubleClick={() =>
					setView((v) => (v.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: DOUBLE_TAP_SCALE, x: 0, y: 0 }))
				}
				onWheel={onWheel}
			>
				<img
					src={src}
					alt='Reported problem'
					draggable={false}
					className='max-w-full max-h-full object-contain'
					style={{
						transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
						transition: gesture.current ? 'none' : 'transform 150ms ease-out',
					}}
				/>
			</div>
		</div>
	);
};

export default PhotoViewer;
